import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Typography,
  App,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  supplierDebitNoteService,
  type SupplierDebitNoteDTO,
  type DebitNoteFilters,
} from '@/services/supplierDebitNote.service';
import { useCurrency } from '@/contexts/CurrencyContext';

const { Title, Text } = Typography;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

export default function SupplierDebitNotesPage() {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { formatCurrency } = useCurrency();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [dateFrom, setDateFrom] = useState<string | undefined>(undefined);
  const [dateTo, setDateTo] = useState<string | undefined>(undefined);

  const filters: DebitNoteFilters = {
    status: statusFilter,
    date_from: dateFrom,
    date_to: dateTo,
  };

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['supplier-debit-notes', filters],
    queryFn: () => supplierDebitNoteService.list(filters),
  });

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      Draft: t('statusDraft'),
      Submitted: t('statusSubmitted'),
      Approved: t('statusApproved'),
      Posted: t('statusPosted'),
      Cancelled: t('statusCancelled'),
    };
    return map[s] ?? s;
  };

  const debitNoteTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      CommercialAdjustment: t('debitNoteTypeCommercialAdjustment'),
      RateDifference: t('debitNoteTypeRateDifference'),
      Other: t('debitNoteTypeOther'),
    };
    return map[type] ?? type;
  };

  const handleSubmit = async (id: string) => {
    try {
      await supplierDebitNoteService.submit(id);
      message.success(t('debitNoteSubmitted'));
      queryClient.invalidateQueries({ queryKey: ['supplier-debit-notes'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('errSubmit'));
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await supplierDebitNoteService.approve(id);
      message.success(t('debitNoteApproved'));
      queryClient.invalidateQueries({ queryKey: ['supplier-debit-notes'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('errApprove'));
    }
  };

  const handlePost = async (id: string) => {
    try {
      await supplierDebitNoteService.post(id);
      message.success(t('debitNotePosted'));
      queryClient.invalidateQueries({ queryKey: ['supplier-debit-notes'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('errPost'));
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await supplierDebitNoteService.cancel(id);
      message.success(t('debitNoteCancelled'));
      queryClient.invalidateQueries({ queryKey: ['supplier-debit-notes'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('errCancel'));
    }
  };

  const columns: ColumnsType<SupplierDebitNoteDTO> = [
    {
      title: t('dnNumber'),
      dataIndex: 'debit_note_number',
      key: 'debit_note_number',
      width: 120,
      render: (val, record) => (
        <Button type="link" size="small" onClick={() => navigate(`/fin/debit-notes/${record.id}`)}>
          {val}
        </Button>
      ),
    },
    {
      title: t('date'),
      dataIndex: 'debit_note_date',
      key: 'debit_note_date',
      width: 110,
      render: (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: t('supplier'),
      key: 'supplier',
      render: (_, r) => r.supplier?.supplier_name ?? r.supplier_id ?? '—',
    },
    {
      title: t('total'),
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 110,
      align: 'right',
      render: (v: number) => formatCurrency(Number(v)),
    },
    {
      title: t('type'),
      dataIndex: 'debit_note_type',
      key: 'debit_note_type',
      width: 120,
      render: (v: string) => debitNoteTypeLabel(v),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string) => <Tag color={statusColors[s] ?? 'default'}>{statusLabel(s)}</Tag>,
    },
    {
      title: tc('actions'),
      key: 'actions',
      width: 280,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/fin/debit-notes/${record.id}`)}>
            {tc('view')}
          </Button>
          {record.status === 'Draft' && (
            <>
              <Button type="link" size="small" onClick={() => handleSubmit(record.id)}>{tc('submit')}</Button>
              <Button type="link" size="small" onClick={() => handleApprove(record.id)}>{tc('approve')}</Button>
              <Button type="link" size="small" onClick={() => handlePost(record.id)}>{tc('post')}</Button>
            </>
          )}
          {(record.status === 'Submitted' || record.status === 'Approved') && (
            <Button type="link" size="small" onClick={() => handlePost(record.id)}>{tc('post')}</Button>
          )}
          {!['Posted', 'Cancelled'].includes(record.status) && (
            <Button type="link" size="small" danger onClick={() => handleCancel(record.id)}>{tc('cancel')}</Button>
          )}
          <Button type="link" size="small" icon={<PrinterOutlined />} onClick={() => navigate(`/fin/debit-notes/${record.id}`)}>
            {t('print')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Title level={4} style={{ margin: 0 }}>
            {t('supplierDebitNotes')}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            {t('supplierDebitNotesHint')}
          </Text>
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16 }}
            message={t('debitNoteRegister')}
            description={t('debitNoteRegisterDesc')}
          />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/fin/debit-notes/new')}>
            {t('createDebitNote')}
          </Button>
        </Col>
      </Row>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder={t('status')}
                allowClear
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'Draft', label: t('statusDraft') },
                  { value: 'Submitted', label: t('statusSubmitted') },
                  { value: 'Approved', label: t('statusApproved') },
                  { value: 'Posted', label: t('statusPosted') },
                  { value: 'Cancelled', label: t('statusCancelled') },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                type="date"
                placeholder={t('fromDate')}
                value={dateFrom ?? ''}
                onChange={(e) => setDateFrom(e.target.value || undefined)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                type="date"
                placeholder={t('toDate')}
                value={dateTo ?? ''}
                onChange={(e) => setDateTo(e.target.value || undefined)}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>

          <Table
            loading={isLoading}
            columns={columns}
            dataSource={list.map((o) => ({ ...o, key: o.id }))}
            sticky={{ offsetHeader: 0 }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => t('totalDebitNotes', { count: total }),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            size="small"
            scroll={{ x: 1100, y: 520 }}
          />
        </Space>
      </Card>
    </div>
  );
}
